import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { devLoginAs, type ErpRole } from '../lib/auth-client';
import { useAuth } from '../context/AuthContext';

interface QuickUser {
  key: ErpRole;
  role: string;
  name: string;
}

const QUICK_USERS: QuickUser[] = [
  { key: 'KTT', role: 'Kế toán trưởng', name: 'Nguyễn Thị Hà' },
  { key: 'NV_KT', role: 'NV Kế toán', name: 'Lê Huỳnh Diệu' },
  { key: 'CEO', role: 'CEO', name: 'Phạm Minh Đức' },
  { key: 'NV', role: 'Nhân viên', name: 'Trần Thị B' },
];

const IS_DEMO = () => {
  const host = window.location.hostname;
  return host === '127.0.0.1' || host === 'localhost';
};

export function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const showDemo = IS_DEMO();

  const handleQuickLogin = (role: ErpRole) => {
    setBusy(true); setErr(null);
    try {
      const u = devLoginAs(role);
      setUser(u);
      const home = u.role === 'NV' ? '/mypayslips' : '/dashboard';
      navigate(home, { replace: true });
    } finally { setBusy(false); }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!email || !password) return setErr('Nhập email + mật khẩu.');
    if (showDemo && password === 'demo123') {
      // Demo: map email → role
      const map: Record<string, ErpRole> = {
        'hant@hitek.vn': 'KTT', 'ha@hitek.vn': 'KTT',
        'dieu@hitek.vn': 'NV_KT', 'nvkt@hitek.vn': 'NV_KT',
        'duc@hitek.vn': 'CEO', 'ceo@hitek.vn': 'CEO',
        'b@hitek.vn': 'NV', 'nv@hitek.vn': 'NV',
      };
      const role = map[email.toLowerCase()] || 'NV';
      return handleQuickLogin(role);
    }
    // Real SSO flow — chưa wire, chuyển sang error
    setErr('SSO thật chưa nối. Dev mode: mật khẩu demo123 + email trong danh sách quick user, hoặc bấm 1 quick role.');
  };

  return (
    <main className="login-page">
      <section className="login-card">
        {/* LEFT — visual gradient */}
        <aside className="login-visual">
          <div className="login-illustration" aria-hidden="true">
            <div className="login-chart-window">
              <span />
              <div>
                <i /><i /><i /><i />
              </div>
            </div>
            <div className="login-person person-one" />
            <div className="login-person person-two" />
            <div className="login-person person-three" />
          </div>
          <div className="login-benefits">
            <div>
              <b>✓</b>
              <span>
                <strong>{showDemo ? 'Quick role testing' : 'Truy cập an toàn'}</strong>
                <small>{showDemo ? 'Chọn 1 vai trò để trải nghiệm quyền hạn tương ứng' : 'Đăng nhập một lần qua SSO Launcher · Session mã hoá'}</small>
              </span>
            </div>
            <div>
              <b>✓</b>
              <span>
                <strong>Trung tâm phê duyệt 2 luồng</strong>
                <small>Duyệt gấp trong ngày · Batch T6 cho chi thường + tạm ứng</small>
              </span>
            </div>
            <div>
              <b>✓</b>
              <span>
                <strong>Tuân thủ TT200 · 2014/TT-BTC</strong>
                <small>COA đầy đủ · P&L · CĐKT · Lưu chuyển tiền tệ · Thuyết minh BCTC</small>
              </span>
            </div>
          </div>
        </aside>

        {/* RIGHT — form panel */}
        <section className="login-form-panel">
          <div className="login-brand-title">
            <span>HITEK</span>
            <i>Kế toán.</i>
          </div>
          <h1>Đăng nhập ERP Kế toán</h1>
          <p>{showDemo ? 'Dev mode — bấm 1 role hoặc nhập demo123' : 'Đăng nhập bằng tài khoản HITEK để tiếp tục'}</p>

          {showDemo && (
            <div className="quick-login-row">
              {QUICK_USERS.map((u) => (
                <button
                  key={u.key}
                  type="button"
                  disabled={busy}
                  onClick={() => handleQuickLogin(u.key)}
                >
                  <strong>{u.role}</strong>
                  <span>{u.name}</span>
                </button>
              ))}
            </div>
          )}

          <form className="login-form" onSubmit={handleSubmit}>
            <label>
              <span>Email</span>
              <input
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@hitek.vn"
                autoComplete="email"
              />
            </label>
            <label>
              <span>Mật khẩu</span>
              <input
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={showDemo ? 'demo123' : 'Mật khẩu'}
                autoComplete="current-password"
              />
            </label>
            {err && <div className="login-error">{err}</div>}
            <button className="login-submit" type="submit" disabled={busy}>
              {busy ? 'Đang đăng nhập…' : 'Đăng nhập'}
            </button>
          </form>

          {showDemo && (
            <p className="login-hint">
              Mật khẩu demo: <strong>demo123</strong>
            </p>
          )}
        </section>
      </section>
    </main>
  );
}
